<template>
  <div class="relative flex flex-col justify-between flex-1 w-full h-full min-h-full overflow-visible">
    <div class="w-full h-[59px] border-t border-b absolute top-0 z-20 pointer-events-none" />
    <div class="relative overflow-auto h-full mb-[64px] max-w-full pt-0" :class="[`${$store.state.ui.darkMode ? 'dark-scrollbar' : ''}`]">
      <table :id="component_reference_slug" class="min-w-full table-fixed">
        <thead
          id="table-header"
          class="sticky top-0 z-20 w-full transition ease-in-out bg-light-box-background dark:bg-dark-box-background duration-side-menu-transition-duration">
          <draggable
            v-model="headers"
            :component-data="{
              type: 'transition-group',
              name: !drag ? 'flip-list' : null
            }"
            v-bind="dragOptions"
            handle=".drag-handle"
            filter=".ignore-drag"
            item-key="id"
            class="text-left transition ease-in-out border-light-divider-border dark:border-dark-divider-border text-common-grey duration-side-menu-transition-duration"
            tag="tr"
            @start="drag = true"
            @end="
              () => {
                drag = false
                updateCurrentViewMetrics()
              }
            ">
            <template #item="{ element, index }">
              <th
                v-if="element.type == 'checkbox' && !hideCheckboxes && !editing_disabled"
                :class="[
                  { 'opacity-0 pointer-events-none': !current_records.length },
                  {
                    'group/recordselection': !select_all_visible_records && !select_all_records
                  },
                  {
                    '!rounded-tr-md': index == headers.length - 1 || (hideCheckboxes && index == 0),
                    'freeze-column bg-white dark:bg-dark-box-background': $store.state.ui_options?.frozen_columns?.length
                  }
                ]"
                class="relative w-5 px-6 rounded-md ignore-drag non-draggable">
                <div class="relative z-40 flex items-center space-x-2">
                  <input
                    v-if="select_all_records"
                    :checked="select_all_records && !excluded_records.length"
                    type="checkbox"
                    class="form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] rounded-l-none focus:ring-0 ring-dark/0 border-[#c9d1d5] w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration"
                    @input="handleSelectOrUnselectAllRecords">
                  <input
                    v-else
                    :checked="all_visible_records_selected"
                    type="checkbox"
                    class="form-checkbox text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] rounded-l-none focus:ring-0 ring-dark/0 border-[#c9d1d5] w-5 rounded h-5 transition ease-in-out duration-side-menu-transition-duration"
                    @input="
                      () => {
                        all_visible_records_selected ? handleUnSelectVisibleRecords() : handleSelectVisibleRecords()
                      }
                    ">

                  <Menu ref="goalsMenu" as="div" class="absolute !z-50 flex-shrink-0 top-[0px] -left-[28px]">
                    <MenuButton
                      class="bg-[#c9d1d5] focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                      <span><ChevronDown class="w-5 h-5 py-1 border-l-none text-common-grey" /></span>
                    </MenuButton>
                    <transition
                      enter-active-class="transition duration-100 ease-out"
                      enter-from-class="transform scale-95 opacity-0"
                      enter-to-class="transform scale-100 opacity-100"
                      leave-active-class="transition duration-75 ease-in"
                      leave-from-class="transform scale-100 opacity-100"
                      leave-to-class="transform scale-95 opacity-0">
                      <MenuItems
                        class="absolute z-50 w-48 mx-3 mt-4 -ml-0 font-normal origin-bottom-right bg-white rounded-lg shadow-lg cursor-pointer focus:outline-none">
                        <MenuItem
                          as="div"
                          :class="{ 'font-bold': all_visible_records_selected }"
                          class="flex items-center p-2 space-x-2 hover:bg-gray-100"
                          @click="
                            () => {
                              select_all_records = false
                              all_visible_records_selected ? handleUnSelectVisibleRecords() : handleSelectVisibleRecords()
                            }
                          ">
                          <Check class="mr-2 w-4 h-4" />
                          {{ all_visible_records_selected ? $t("Unselect") : $t("Select") }}
                          {{ $t("this page") }}
                        </MenuItem>
                        <MenuItem
                          as="div"
                          :class="{
                            'font-bold': select_all_records && !excluded_records.length
                          }"
                          class="flex items-center p-2 space-x-2 hover:bg-gray-100"
                          @click="
                            () => {
                              handleUnSelectVisibleRecords()

                              handleSelectOrUnselectAllRecords()
                            }
                          ">
                          <CheckCheck class="mr-3 w-4 h-4" />
                          {{ select_all_records && !excluded_records.length ? $t("Unselect") : $t("Select") }}
                          {{ $t("all") }}
                        </MenuItem>
                        <MenuItem
                          as="div"
                          class="flex items-center p-2 hover:bg-gray-100"
                          @click="
                            () => {
                              handleUnSelectVisibleRecords()
                              select_all_records = false
                            }
                          ">
                          <X class="mr-3 w-4 h-4" color="#899399" /> {{ $t("Clear selection") }}
                        </MenuItem>
                      </MenuItems>
                    </transition>
                  </Menu>
                </div>
              </th>

              <th
                v-else-if="element.type == 'metric' || element.type == 'field'"
                :class="{
                  '!rounded-tr-md': index == headers.length - 1 || (hideCheckboxes && index == 0),
                  '!bg-gray-300': element.is_highlighted,
                  'freeze-second-column  bg-white dark:bg-dark-box-background': $store.state.ui_options?.frozen_columns?.find(
                    (x) => (x.object_type == 'record' || x.object_type == objectType) && x.id == element.id
                  )
                }"
                class="mr-20 font-semibold text-gray-500 bg-white whitespace-nowrap group/row"
                @click.stop="() => {}">
                <div class="flex justify-between w-full space-x-4">
                  <div
                    class="relative flex items-center w-full py-4"
                    @click.stop="
                      ($event) => {
                        $event?.ctrlKey ? openEditMetric(element) : $event?.altKey ? highlightRelatedMetrics(element) : void null
                      }
                    "
                    @mouseout="$event.target.classList.remove('cursor-pointer', 'underline', 'text-blue')"
                    @mouseover="
                      ($event) => {
                        $event?.ctrlKey || $event?.altKey
                          ? $event.target.classList.add('cursor-pointer', 'underline', 'text-blue')
                          : $event.target.classList.remove('cursor-pointer', 'underline', 'text-blue')
                      }
                    ">
                    <button
                      class="absolute h-12 drag-handle w-[105%] text-gray-800 dark:hover:text-white cursor-move active:text-gray-500" />
                    <span class="relative ml-4 mr-4"> {{ $utils.getRecordName(element) }}</span>
                  </div>

                  <Menu v-slot="{ close }" ref="goalsMenu" as="div" class="relative flex-shrink-0 ml-auto mr-4">
                    <MenuButton
                      :class="{
                        'bg-light-brand-blue': getColumnFilterFromFilter(element)
                      }"
                      class="inline-flex col-table-header-btn items-center justify-center w-10 -mt-[5%] h-[105%] bg-[#F8FAFB] border focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue"
                      @click="click_position = $utils.clone($store.state.ui.cursor_positions?.closest_side)">
                      <ListFilter v-if="getColumnFilterFromFilter(element)" color="white" />
                      <ChevronDown v-else class="w-12 h-12 py-4 text-common-grey" />
                    </MenuButton>
                    <transition
                      enter-active-class="transition duration-100 ease-out"
                      enter-from-class="transform scale-95 opacity-0"
                      enter-to-class="transform scale-100 opacity-100"
                      leave-active-class="transition duration-75 ease-in"
                      leave-from-class="transform scale-100 opacity-100"
                      leave-to-class="transform scale-95 opacity-0">
                      <MenuItems
                        class="absolute font-normal !z-50 w-60 mx-3 mt-0 -ml-28 origin-bottom-right bg-white shadow-lg rounded-lg focus:outline-none">
                        <div class="p-3 hover:bg-gray-100">
                          <Menu v-slot="{ close: closeFilterModal }" ref="goalsMenu" as="div" class="relative flex-shrink-0 ml-auto">
                            <MenuButton
                              class="inline-flex items-center justify-center focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                              <ChevronDown
                                :class="[{ 'rotate-90': click_position == 'right' }, { '-rotate-90': click_position == 'left' }]"
                                class="w-4 h-4 ml-1 mr-5 text-common-grey" />
                              {{ $t("Filter column") }}
                            </MenuButton>
                            <MenuItems
                              :class="[{ '-ml-[307px]': click_position == 'right' }, { 'ml-[232px]': click_position == 'left' }]"
                              class="absolute z-50 mx-3 -mt-12 font-normal origin-bottom-right bg-white rounded-lg shadow-lg w-80 focus:outline-none">
                              <div class="mt-4 mb-4">
                                <span class="m-3">{{ $t("Filter") }} {{ element.name }}</span>
                                <ChevronDown
                                  :class="[{ '-rotate-90': click_position == 'right' }, { 'rotate-90': click_position == 'left' }]"
                                  class="w-5 h-5 ml-auto mr-4 -mt-5 cursor-pointer text-common-grey"
                                  @click="closeFilterModal()" />
                              </div>
                              <TableMetricsFilter
                                :initial-filter="getColumnFilterFromFilter(element)"
                                label=""
                                :metric="element"
                                :show-clear-all="false"
                                :show-buttons="false"
                                :object-type="element.object_type"
                                :placeholder="$t('Search for metrics for filter with')"
                                :disable-add="true"
                                @change="
                                  ($event) => {
                                    if (!$event.valid) {
                                      current_column_filter_valid = false
                                    } else {
                                      handleChangeColumnFilter($event.filter)
                                      current_column_filter_valid = true
                                    }
                                  }
                                " />
                              <div class="flex items-center justify-center w-full mt-4 space-x-6">
                                <button
                                  class="-mt-3 text-sm text-light-brand-blue"
                                  @click="handleRemoveColumnFilter(element, closeFilterModal, close)">
                                  {{ $t("Clear filter") }}
                                </button>

                                <button
                                  :disabled="!current_column_filter || !current_column_filter_valid"
                                  class="mb-4 font-normal btn primary-btn w-44 !z-0"
                                  @click="handleSubmitColumnFilter(closeFilterModal, close)">
                                  {{ $t("Show results") }}
                                </button>
                              </div>
                            </MenuItems>
                          </Menu>
                        </div>
                        <div
                          class="p-3 flex space-x-2 items center hover:bg-[#F8FAFB] cursor-pointer"
                          @click.stop="
                            () => {
                              element.sort_index =
                                element?.sort == 0 || element?.sort == undefined
                                  ? headers.filter((x) => x.sort != 0).length
                                  : element.sort_index
                              element.sort = element.sort == 1 ? 0 : ~-element.sort ^ element.sort
                              sortRecords()
                            }
                          ">
                          <ChevronDown v-if="element.sort == -1" class="w-5 h-5 mr-3 text-common-grey" aria-hidden="true" />
                          <ChevronUp v-else-if="element.sort == 1" class="w-5 h-5 mr-3 text-common-grey" aria-hidden="true" />
                          <ChevronsUpDown v-else class="w-5 h-5 mr-3 text-common-grey" aria-hidden="true" />

                          <span class="">{{ $t("Sort column") }}</span>
                        </div>
                        <div
                          class="flex items-center p-3 space-x-2 cursor-pointer hover:bg-gray-100"
                          @click="freezeColumn(element.id, close)">
                          <Lock
                            :class="{
                              'text-red-500': $store.state.ui_options?.frozen_columns?.find(
                                (x) => x.object_type == objectType && x.id == element.id
                              )
                            }"
                            class="w-4 h-4 ml-1 mr-5 text-common-grey"
                            aria-hidden="true" />
                          {{
                            $store.state.ui_options?.frozen_columns?.find((x) => x.object_type == objectType && x.id == element.id)
                              ? $t("Unlock")
                              : $t("Lock")
                          }}
                          {{ $t("column") }}
                        </div>
                      </MenuItems>
                    </transition>
                  </Menu>
                </div>
              </th>
            </template>
          </draggable>
        </thead>
        <!-- Global Table - rows -->

        <tbody
          v-if="current_records.length > 0"
          id="records-wrapper"
          class="h-full transition ease-in-out divide-y rounded-lg bg-light-box-background divide-light-divider-border dark:divide-dark-divider-border dark:bg-dark-box-background duration-side-menu-transition-duration">
          <tr
            v-for="(record, index) in current_records"
            :id="record.id"
            :key="index"
            v-prevent-drag-click
            class="cursor-default w-full text-sm transition ease-in-out rounded-lg shadow-sm group/row text-light-body-text dark:text-dark-body-text hover:bg-[#F8FAFB] dark:hover:bg-dark-brand-blue"
            @contextmenu.prevent="handleContextMenu($event, record)">
            <td
              v-if="!hideCheckboxes && !editing_disabled"
              :class="{
                'freeze-column bg-white dark:bg-dark-box-background group-hover/row:bg-[#F8FAFB] dark:group-hover/row:bg-dark-brand-blue transition ease-in-out':
                  $store.state.ui_options?.frozen_columns?.length
              }"
              class="relative flex items-center w-5 px-6 py-3 h-14"
              @click.stop="() => {}">
              <input
                v-if="select_all_records"
                :checked="!excluded_records.includes(record.id)"
                type="checkbox"
                class="form-checkbox duration-75 dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-[#c9d1d5] w-5 rounded h-5"
                @click="toggleRecordFromSelectedAllList(record)">

              <input
                v-else
                :checked="selected_records.has(record.id)"
                type="checkbox"
                class="form-checkbox duration-75 text-light-brand-blue dark:text-dark-brand-blue checked:bg-light-brand-blue dark:checked:bg-dark-brand-blue bg-light-box-background dark:bg-dark-box-background border-[2px] focus:ring-0 ring-dark/0 border-[#c9d1d5] w-5 rounded h-5"
                @click="toggleRecordFromSelectedList(record, $event)">
            </td>

            <!-- Global Table - columns -->
            <td
              v-for="(metric, i) in headers.filter((elm, index) => index > 0 && (elm.type == 'metric' || elm.type == 'field'))"
              :key="metric.id"
              class="px-6 py-3 font-light h-14"
              :class="[
                {
                  'freeze-second-column bg-white dark:bg-dark-box-background group-hover/row:bg-[#F8FAFB] dark:group-hover/row:bg-dark-brand-blue transition ease-in-out':
                    $store.state.ui_options?.frozen_columns?.find(
                      (x) => (x.object_type == 'record' || x.object_type == objectType) && x.id == metric.id
                    )
                },
                {
                  'hover:text-blue hover:underline hover:font-medium':
                    metric.reference_name == utils.getOptionMetricReference('company', 'name')
                }
              ]"
              :style="{
                backgroundColor: `${
                  metric.type == 'metric'
                    ? utils.calculateRecordMetricColor(metric.id, record.values[metric.reference_name])?.option == 'background' ||
                      utils.calculateRecordMetricColor(metric.id, record.values[metric.reference_name])?.option == 'mixed'
                      ? `rgba(${hexToRgb(utils.calculateRecordMetricColor(metric.id, record.values[metric.reference_name]).color).r},${
                        hexToRgb(utils.calculateRecordMetricColor(metric.id, record.values[metric.reference_name]).color).g
                      },${hexToRgb(utils.calculateRecordMetricColor(metric.id, record.values[metric.reference_name]).color).b},${
                        metric.metric_colors.metric_color_display_opacity / 100
                      })`
                      : ''
                    : ''
                }`
              }"
              @click.stop="
                metric.reference_name == utils.getOptionMetricReference('company', 'name')
                  ? openRecordDetailsPage(record, metric)
                  : !metric?.is_read_only
                    ? handleRecordClick(metric, record)
                    : null
              ">
              <div
                v-if="
                  $store.state.ui_options?.frozen_columns?.find(
                    (x) => (x.object_type == 'record' || x.object_type == objectType) && x.id == metric.id
                  )
                "
                class="absolute -right-[0px] -mt-5 h-16 w-2 border-r-2" />

              <div class="relative flex group/edit">
                <RecordAutoCompleteInput
                  v-if="record[metric.reference_name]?.edit_mode && metric.id == 'parent__id'"
                  :filter-metric-ids="[$utils.getMetricByReferenceName($utils.getOptionMetricReference('company', 'name')).id]"
                  :is-async="true"
                  placeholder=""
                  label="Company"
                  :is-box-background="true"
                  :disabled="
                    $route.name == 'CompanyProfile' || ($route.name == 'ObjectProfile' && $route.params.object_type == 'opportunity')
                  "
                  type="text"
                  label-attr="name"
                  classes="w-full block border-none p-0"
                  :value="parent_company"
                  @chosen="
                    ($event) => {
                      parent_company = $event
                    }
                  "
                  @changed="($event) => parentChanged($event)" />
                <MetricFieldInput
                  v-else-if="record[metric.reference_name]?.edit_mode && metric.type != 'field'"
                  class="min-w-[120px] max-w-[35vw] w-full ml-auto"
                  :class="[
                    {
                      'bg-white dark:bg-dark-box-background rounded-lg':
                        record[metric.reference_name]?.edit_mode && metric?.input_type == 'StarRating'
                    },
                    {
                      'min-w-[120px]':
                        record[metric.reference_name]?.edit_mode &&
                        (metric?.input_type == 'Checklist' ||
                          metric?.input_type == 'BulletPointsList' ||
                          metric?.input_type == 'OrderedList' ||
                          metric?.input_type == 'BarRating')
                    }
                  ]"
                  :show-label="false"
                  :label="'Input'"
                  :metric="metric_to_be_edited"
                  :component-reference="componentReference"
                  @update:metric-value="($event) => updateNewMetricValue($event)"
                  @click.stop=""
                  @keyup.enter="
                    () => {
                      if (metric?.input_type != 'Textarea' && metric?.input_type != 'Checklist') {
                        updateRecordMetricValue(record)
                      }
                    }
                  "
                  @keyup.esc="cancelEditMode(metric, record)" />

                <template v-else>
                  <!-- Adding color and logo if the metric is name metric -->

                  <div v-if="metric.reference_name == utils.getOptionMetricReference(objectType, 'name')" class="float-left">
                    <span
                      v-if="utils.getDomainName(record.values[utils.getOptionMetricReference('company', 'website')])"
                      class="float-left !w-10">
                      <img
                        onerror="this.style.display='none'"
                        class="inline-block object-contain rounded-md !h-9 !min-h-9 !max-h-9 w-9 !min-w-9 !max-w-9"
                        :src="`https://logosnap.io/api/${utils.getDomainName(
                          record.values[utils.getOptionMetricReference('company', 'website')]
                        )}?token=ecoTB81m5dQqkrjw`"
                        alt="logo">
                    </span>
                    <span
                      v-if="
                        $store.state.metrics.find((m) => m.metric_colors?.is_primary_metric_color == true && m.object_type == objectType)
                      "
                      class="flex items-center justify-center text-center rounded-md h-9 w-9">
                      <span
                        :style="{
                          background: utils.calculateRecordMetricColor(
                            $store.state.metrics.find(
                              (m) => m.metric_colors?.is_primary_metric_color == true && m.object_type == objectType
                            )?.id,
                            record.values[
                              $store.state.metrics.find(
                                (m) => m.metric_colors?.is_primary_metric_color == true && m.object_type == objectType
                              )?.reference_name
                            ]
                          )?.color
                        }"
                        class="flex-shrink-0 inline-block w-3 h-3 rounded-full opacity-90" />
                    </span>
                  </div>
                  <!-- end -->
                  <div
                    v-if="metric.id == 'owner__id'"
                    :class="{
                      'freeze-second-column bg-white dark:bg-dark-box-background group-hover/row:bg-[#F8FAFB] dark:group-hover/row:bg-dark-brand-blue transition ease-in-out':
                        $store.state.ui_options?.frozen_columns?.find((x) => x.object_type == objectType && x.id == 'owner__id')
                    }"
                    class="font-light min-w-[250px]">
                    <div class="relative flex items-center">
                      <div
                        v-if="$store.state.ui_options?.frozen_columns?.find((x) => x.object_type == objectType && x.id == 'owner__id')"
                        class="absolute w-2 h-16 -mt-5 border-r-2 right-4" />
                      <template v-if="record.owner">
                        <Avatar
                          :avatar="utils.getUserById(record.owner, 'avatar')"
                          :hover-effect="true"
                          :first-name="utils.getUserById(record.owner, 'first_name')"
                          :last-name="utils.getUserById(record.owner, 'last_name')" />
                        <div class="ml-3">
                          <p class="h-full text-sm font-normal text-gray-700 dark:text-white">
                            {{ utils.getUserById(record.owner, "full_name") }}
                          </p>
                          <div class="flex items-center text-xs text-common-grey whitespace-nowrap">
                            <Clock class="w-3 h-3 mr-1" /> {{ $t("Last login") }}
                            {{
                              utils.getUserById(record.owner, "last_login")
                                ? $moment(utils.getUserById(record.owner, "last_login")).fromNow()
                                : $t("never")
                            }}
                          </div>
                        </div>
                      </template>
                      <div v-else>
                        -
                      </div>
                    </div>
                  </div>
                  <div
                    v-else-if="metric.id == 'co_owners__id'"
                    :class="{
                      'freeze-second-column bg-white dark:bg-dark-box-background group-hover/row:bg-[#F8FAFB] dark:group-hover/row:bg-dark-brand-blue transition ease-in-out':
                        $store.state.ui_options?.frozen_columns?.find((x) => x.object_type == objectType && x.id == 'co_owners__id')
                    }"
                    class="font-light">
                    <div class="flex items-center space-x-2">
                      <div
                        v-if="$store.state.ui_options?.frozen_columns?.find((x) => x.object_type == objectType && x.id == 'co_owners__id')"
                        class="absolute w-2 h-16 -mt-5 border-r-2 right-6" />

                      <div class="flex flex-shrink-0 -space-x-2">
                        <span v-for="user in record.co_owners.slice(0, 5)" :key="user" class="inline-block">
                          <Avatar
                            :first-name="Record.from(user).first_name"
                            :last-name="Record.from(user).last_name"
                            :image="Record.from(user).avatar"
                            :email="Record.from(user).email"
                            :show-tooltip="true" />
                        </span>
                      </div>
                      <span v-if="record.co_owners.length > 5"
                            class="flex-shrink-0 text-xs font-medium leading-5">+{{ record.co_owners.length - 5 }}</span>
                      <span v-if="record.co_owners.length == 0" class="flex-shrink-0 text-xs font-medium leading-5">-</span>
                    </div>
                  </div>
                  <div
                    v-else-if="metric.id == 'tags__name'"
                    :class="{
                      'freeze-second-column bg-white dark:bg-dark-box-background group-hover/row:bg-[#F8FAFB] dark:group-hover/row:bg-dark-brand-blue transition ease-in-out':
                        $store.state.ui_options?.frozen_columns?.find((x) => x.object_type == objectType && x.id == '__tags')
                    }"
                    class="font-light">
                    <div class="flex">
                      <div
                        v-if="$store.state.ui_options?.frozen_columns?.find((x) => x.object_type == objectType && x.id == '__tags')"
                        class="absolute right-0 w-2 h-16 -mt-5 border-r-2" />

                      <div v-if="record?.tags?.length" class="flex flex-row gap-1 flex-nowrap">
                        <div
                          v-for="tag in record?.is_all_assigned_tags_shown ? record?.tags : record?.tags?.slice(0, 2)"
                          :key="tag"
                          class="flex justify-between px-2 py-2 pl-3 text-left rounded-lg dark:text-dark-body-text focus:outline-none"
                          :style="{
                            background: `${$store.state.data.tags.find((t) => t.id == tag)?.color}0F`
                          }">
                          <span class="flex items-center mr-1">
                            <span
                              class="mr-2 w-3.5 h-3.5 rounded-full"
                              :style="{
                                background: $store.state.data.tags.find((t) => t.id == tag)?.color
                              }" />
                            <span
                              class="block font-bold truncate"
                              :style="{
                                color: $store.state.data.tags.find((t) => t.id == tag)?.color
                              }">{{ $store.state.data.tags.find((t) => t.id == tag)?.name }}</span>
                          </span>
                        </div>
                        <div
                          v-if="record?.tags?.length > 2 && !record?.is_all_assigned_tags_shown"
                          class="flex items-center flex-none mr-1 font-normal group/tags flex-nowrap dark:text-white">
                          <span class="block group-hover/tags:hidden">+{{ record?.tags?.length - 2 }} {{ $t("more") }}</span>
                          <div class="relative" @click.stop="showAllAssignedTags(record)">
                            <transition
                              class="hidden group-hover/tags:block"
                              mode="out-in"
                              enter-active-class="animate__animated animate__fadeIn"
                              leave-active-class="animate__animated animate__fadeOut"
                              duration="100">
                              <button
                                class="z-30 flex items-center justify-center w-7 h-7 p-1.5 ml-2 transition-all border rounded-md opacity-0 group-hover/tags:delay-300 group-hover/tags:duration-300 group-hover/tags:opacity-100 group-hover/tags:scale-125 group-hover/tags:bg-gray-200">
                                <ArrowLeftFromLine
                                  class="absolute inset-0 w-5 h-5 m-auto text-white"
                                  :class="[$store.state.portfolio.ui_options?.language == 'ar' ? '' : 'rotate-180']" />
                              </button>
                            </transition>
                          </div>
                        </div>
                        <div
                          v-else-if="record?.tags?.length > 2 && record?.is_all_assigned_tags_shown"
                          class="flex items-center flex-none mr-1 font-normal flex-nowrap dark:text-white"
                          @click.stop="showAllAssignedTags(record)">
                          <div class="relative">
                            <transition
                              mode="out-in"
                              enter-active-class="animate__animated animate__fadeIn"
                              leave-active-class="animate__animated animate__fadeOut"
                              duration="100">
                              <button
                                class="z-30 hover:scale-125 flex items-center justify-center w-7 h-7 p-1.5 transition-all hover:bg-gray-200 border rounded-md">
                                <ArrowLeftFromLine
                                  class="absolute inset-0 w-5 h-5 m-auto text-gray-500 hover:text-white"
                                  :class="[$store.state.portfolio.ui_options?.language == 'ar' ? 'rotate-180' : '']" />
                              </button>
                            </transition>
                          </div>
                        </div>
                      </div>
                      <div v-else class="flex flex-row items-center flex-none gap-1 pl-2 flex-nowrap">
                        -
                      </div>
                      <div class="flex items-center">
                        <button
                          class="opacity-0 hover:scale-125 group-hover/edit:scale-15 group-hover/edit:delay-300 group-hover/edit:duration-300 group-hover/edit:opacity-100 z-30 ml-6 group-hover/edit:scale-15 hover:bg-gray-200 border rounded-md flex items-center justify-center w-7 h-7 p-1.5 transition-all"
                          @click.stop="
                            $emitter.emit('assignTags', {
                              full_records: null,
                              records: null,
                              record: record,
                              object_type: objectType
                            })
                          ">
                          <Pencil v-if="record?.tags?.length" class="w-5 h-5" aria-hidden="true" color="#899399" />
                          <Plus v-else class="w-5 h-5" aria-hidden="true" color="#899399" />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div
                    v-else-if="metric.id == 'parent__id'"
                    :class="{
                      'freeze-second-column bg-white dark:bg-dark-box-background group-hover/row:bg-[#F8FAFB] dark:group-hover/row:bg-dark-brand-blue transition ease-in-out ':
                        $store.state.ui_options?.frozen_columns?.find((x) => x.object_type == objectType && x.id == '_parent')
                    }"
                    class="font-light min-w-[250px]">
                    <div class="relative flex items-center">
                      <div
                        v-if="$store.state.ui_options?.frozen_columns?.find((x) => x.object_type == objectType && x.id == '_parent')"
                        class="absolute w-2 h-16 -mt-5 border-r-2 right-4" />
                      <span
                        class="font-normal transition ease-in-out no-underline decoration-2 whitespace-nowrap overflow-hidden text-ellipsis max-w-[400px]"
                        :class="[record.parent ? 'hover/edit:font-bold hover/edit:underline cursor-pointer' : '']"
                        @click.stop="
                          () => {
                            record.parent ? openRecordParentDetailsPage(record) : null
                          }
                        ">
                        {{ record.parent ? record.parent_name : "-" }}
                      </span>
                    </div>
                  </div>
                  <div
                    v-else-if="metric.id == 'top_parent'"
                    :class="{
                      'freeze-second-column bg-white dark:bg-dark-box-background group-hover/row:bg-[#F8FAFB] dark:group-hover/row:bg-dark-brand-blue transition ease-in-out ':
                        $store.state.ui_options?.frozen_columns?.find((x) => x.object_type == objectType && x.id == 'top_parent')
                    }"
                    class="font-light min-w-[250px]">
                    <div class="relative flex items-center">
                      <div
                        v-if="$store.state.ui_options?.frozen_columns?.find((x) => x.object_type == objectType && x.id == 'top_parent')"
                        class="absolute w-2 h-16 -mt-5 border-r-2 right-4" />
                      <span
                        class="font-normal transition ease-in-out no-underline decoration-2 whitespace-nowrap overflow-hidden text-ellipsis max-w-[400px]"
                        :class="[record.top_parent ? 'hover/edit:font-bold hover/edit:underline cursor-pointer' : '']"
                        @click.stop="
                          () => {
                            record.top_parent ? openRecordParentDetailsPage(record) : null
                          }
                        ">
                        {{ record.top_parent ? record.top_parent_name : "-" }}
                      </span>
                    </div>
                  </div>
                  <div v-else-if="metric.id == 'origins'">
                    <span v-if="record?.origins">
                      <span
                        v-for="[o, o_id] in Object.entries(record.origins).reverse()"
                        :key="o_id"
                        class="font-normal capitalize transition ease-in-out no-underline decoration-2 whitespace-nowrap overflow-hidden text-ellipsis max-w-[400px] mr-1">
                        {{ getServiceTitle(o?.replace("ml.", "")) }}
                      </span>
                    </span>
                  </div>
                  <div v-else-if="utils.isEmpty(record.values[metric.reference_name])" class="w-8 h-4">
                    <span class="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium text-gray-800"> - </span>
                  </div>
                  <template v-else-if="metric?.input_type == 'StarRating'">
                    <div class="flex justify-start w-full">
                      <span v-for="f in (metric, record.values[metric.reference_name])" :key="f" :class="{ 'mr-1': f < 5 }">
                        <svg
                          class="block w-5 h-5"
                          :class="[
                            (metric, record.values[metric.reference_name]) >= f
                              ? (metric, record.values[metric.reference_name]) >= 4
                                ? 'text-status-success-green'
                                : (metric, record.values[metric.reference_name]) >= 3
                                  ? 'text-status-warning-yellow'
                                  : 'text-status-error-red'
                              : 'text-gray-300'
                          ]"
                          fill="currentColor"
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 20 20">
                          <path
                            d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                        </svg>
                      </span>
                    </div>
                  </template>

                  <template v-else-if="metric?.input_type == 'BarRating'">
                    <div class="flex justify-start w-full">
                      <span> {{ record.values[metric.reference_name] }}/10 </span>
                    </div>
                  </template>

                  <template v-else-if="metric?.input_type == 'Checkbox'">
                    <div class="flex justify-start w-full">
                      <input
                        class="rounded text-blue-light"
                        :disabled="true"
                        type="checkbox"
                        :checked="
                          record.values[metric.reference_name] == true ||
                            record.values[metric.reference_name] == 'Yes' ||
                            record.values[metric.reference_name] == 'yes' ||
                            record.values[metric.reference_name] == 'true' ||
                            record.values[metric.reference_name] == 'True'
                        ">
                    </div>
                  </template>

                  <template v-else-if="['DateTime', 'Date'].includes(metric?.data_type)">
                    <div class="flex justify-start w-full whitespace-nowrap">
                      {{ formatMetricDateValue(record, metric) }}
                    </div>
                  </template>

                  <template v-else-if="metric?.input_type == 'ObjectReference'">
                    <div class="flex justify-start w-full">
                      <template
                        v-if="
                          (record.values[metric.reference_name]?.object_type?.toLowerCase() == 'user' ||
                            (record.values[metric.reference_name].origin == 'hubspot' &&
                              record.values[metric.reference_name].object_type == 'owners')) &&
                            record.values[metric.reference_name]?.origin_id
                        ">
                        <div class="font-light min-w-[250px]">
                          <div
                            class="flex items-center cursor-pointer"
                            @click="openObjectReferenceObjectPage(record, record.values[metric.reference_name])">
                            <Avatar
                              :avatar="
                                utils.getUserByOriginId(
                                  record.values[metric.reference_name].origin_id,
                                  record.values[metric.reference_name].origin,
                                  'avatar'
                                )
                              "
                              :hover-effect="true"
                              :first-name="
                                utils.getUserByOriginId(
                                  record.values[metric.reference_name].origin_id,
                                  record.values[metric.reference_name].origin,
                                  'first_name'
                                ) ?? record.values[metric.reference_name].name.split(' ')?.[0]
                              "
                              :last-name="
                                utils.getUserByOriginId(
                                  record.values[metric.reference_name].origin_id,
                                  record.values[metric.reference_name].origin,
                                  'last_name'
                                ) ??
                                  record.values[metric.reference_name].name.split(' ')?.[
                                    record.values[metric.reference_name].name.split(' ').length - 1
                                  ]
                              " />
                            <div class="ml-3">
                              <p class="h-full text-sm font-normal text-gray-700 dark:text-white">
                                {{ record.values[metric.reference_name].name }}
                              </p>
                            </div>
                          </div>
                        </div>
                      </template>
                      <template v-else-if="record.values[metric.reference_name]?.name && record.values[metric.reference_name]?.origin_id">
                        <div class="font-light min-w-[250px]">
                          <div
                            class="flex items-center cursor-pointer"
                            @click="openObjectReferenceObjectPage(record, record.values[metric.reference_name])">
                            <!-- <img v-if="utils.getUserByOriginId(record.values[metric.reference_name].origin_id, 'avatar')" class="inline-block rounded-full h-9 w-9 object-cover" :src="utils.getUserByOriginId(record.values[metric.reference_name].origin_id, 'avatar')" alt=""> -->
                            <Avatar :hover-effect="true" :is-company="true" :name="record.values[metric.reference_name].name" />
                            <div class="ml-3">
                              <p class="h-full text-sm font-normal text-gray-700 dark:text-white">
                                {{ record.values[metric.reference_name].name }}
                              </p>
                            </div>
                          </div>
                        </div>
                      </template>
                      <template v-else-if="record.values[metric.reference_name]?.origin_id">
                        <div class="font-light min-w-[250px]">
                          <div
                            class="flex items-center cursor-pointer"
                            @click="openObjectReferenceObjectPage(record, record.values[metric.reference_name])">
                            <Avatar
                              :avatar="utils.getUserByOriginId(record.values[metric.reference_name].origin_id, 'avatar')"
                              :hover-effect="true"
                              :first-name="utils.getUserByOriginId(record.values[metric.reference_name].origin_id, 'first_name')"
                              :last-name="utils.getUserByOriginId(record.values[metric.reference_name].origin_id, 'last_name')" />
                            <div class="ml-3">
                              <p class="h-full text-sm font-normal text-gray-700 dark:text-white">
                                {{ record.values[metric.reference_name].name }}
                              </p>
                            </div>
                          </div>
                        </div>
                      </template>
                      <template v-else>
                        <span class="inline-flex items-center px-2 py-0.5 rounded text-sm font-medium text-gray-800"> - </span>
                      </template>
                    </div>
                  </template>

                  <div v-else class="flex items-center gap-1 has-tooltip">
                    <span
                      v-if="!i"
                      :style="{ background: record.color }"
                      class="flex-shrink-0 inline-block w-3 h-3 mr-2 rounded-full opacity-90" />
                    <span
                      v-if="
                        utils.calculateRecordMetricColor(metric.id, record.values[metric.reference_name])?.option == 'dot' ||
                          utils.calculateRecordMetricColor(metric.id, record.values[metric.reference_name])?.option == 'mixed'
                      "
                      :style="{
                        background: utils.calculateRecordMetricColor(metric.id, record.values[metric.reference_name]).color
                      }"
                      class="flex-shrink-0 inline-block w-3 h-3 mr-2 rounded-full opacity-90" />
                    <span
                      :style="{
                        color:
                          utils.calculateRecordMetricColor(metric.id, record.values[metric.reference_name])?.option == 'text' ||
                          utils.calculateRecordMetricColor(metric.id, record.values[metric.reference_name])?.option == 'mixed'
                            ? utils.calculateRecordMetricColor(metric.id, record.values[metric.reference_name]).color
                            : ''
                      }"
                      :class="{
                        'font-normal cursor-pointer transition ease-in-out no-underline decoration-2 hover/edit:font-bold hover/edit:underline':
                          metric.reference_name == utils.getOptionMetricReference(objectType, 'name')
                      }"
                      class="whitespace-nowrap overflow-hidden text-ellipsis max-w-[400px] cursor-pointer"
                      @click.stop="
                        () => {
                          if (metric.reference_name == utils.getOptionMetricReference(objectType, 'name')) {
                            openRecordDetailsPage(record)
                          }
                        }
                      ">
                      <span v-if="metric.input_type == 'RichTextarea'">
                        <TruncatedHtml :content="String($utils.humanizeMetricValue(metric, record.values[metric.reference_name]))" />
                      </span>
                      <span v-else>
                        {{ String($utils.humanizeMetricValue(metric, record.values[metric.reference_name])) }}
                      </span>
                    </span>
                    <!-- long name tooltip -->
                    <span
                      v-if="String($utils.humanizeMetricValue(metric, record.values[metric.reference_name])).length > 40"
                      class="tooltip bg-gray-50 dark:bg-gray-700 rounded p-2 whitespace-pre-wrap text-black dark:text-white max-w-[400px]">
                      <span
                        v-if="metric.input_type == 'RichTextarea'"
                        v-html="$utils.humanizeMetricValue(metric, record.values[metric.reference_name])" />
                      <span v-else>
                        {{ $utils.humanizeMetricValue(metric, record.values[metric.reference_name]) }}
                      </span>
                    </span>
                    <!-- end -->

                    <div v-if="metric.reference_name == utils.getOptionMetricReference(objectType, 'name')" class="relative cursor-pointer">
                      <div class="relative ml-1 -mt-4" @click.stop="openModalNotes(record)">
                        <NotesIcon
                          :class="[
                            record?.notes_summary?.count == 0 ? 'opacity-0' : 'opacity-100',
                            'group-hover/edit:opacity-100 mr-2 w-5 h-5'
                          ]" />
                        <div class="flex justify-center items-center absolute left-0.5 top-0">
                          <div class="flex items-center justify-center w-4 h-4 text-xs font-bold text-center text-white rounded-full">
                            <Plus
                              v-if="record?.notes_summary?.count == 0"
                              class="w-5 h-5 text-white transition opacity-0 group-hover/edit:opacity-100" />
                            <div v-else>
                              {{ record?.notes_summary?.count }}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </template>
                <div v-if="!editing_disabled" :class="[` relative`]">
                  <transition
                    mode="out-in"
                    enter-active-class="animate__animated animate__fadeIn"
                    leave-active-class="animate__animated animate__fadeOut"
                    duration="100">
                    <div class="flex items-center space-x-1">
                      <div v-if="!metric_to_be_edited && metric.data_type == 'Number'" class="ml-5" @click.stop>
                        <button
                          :class="{
                            'opacity-100': formulas_open.includes(`${metric.id}>${record.id}`)
                          }"
                          :open="formulas_open.includes(`${metric.id}>${record.id}`)"
                          class="opacity-0 hover:scale-125 group-hover/edit:scale-15 group-hover/edit:delay-300 group-hover/edit:duration-300 group-hover/edit:opacity-100 inline-flex items-center justify-center relative hover:bg-gray-200 w-7 h-7 ml-3 p-1.5 border rounded-md focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue"
                          @click="setFormulaOpen(metric, record)">
                          <Sigma class="w-5 h-5" aria-hidden="true" color="#899399" />
                        </button>
                      </div>

                      <div
                        v-if="
                          metric.id != 'owner__id' &&
                            metric.id != 'co_owners__id' &&
                            metric.id != 'tags__name' &&
                            ((metric.id == 'parent__id' && record.type == 'contact') || metric.id != 'parent__id') &&
                            metric.id != 'top_parent' &&
                            metric.id != 'origins' &&
                            !metric?.is_read_only
                        "
                        :class="{
                          'ml-5': !metric.included_metrics?.length && !metric_to_be_edited
                        }">
                        <button
                          v-if="!record[metric.reference_name]?.edit_mode && !metric_to_be_edited"
                          class="opacity-0 hover:scale-125 group-hover/edit:scale-15 group-hover/edit:delay-300 group-hover/edit:duration-300 group-hover/edit:opacity-100 z-30 hover:bg-gray-200 border rounded-md flex items-center justify-center w-7 h-7 p-1.5 transition-all ease-in-out"
                          @click.stop="toggleEditMode(metric, record)">
                          <Pencil v-if="!metric_to_be_edited" class="w-5 h-5" aria-hidden="true" color="#899399" />
                        </button>
                        <div
                          v-else-if="record[metric.reference_name]?.edit_mode"
                          class="flex items-center scale-125 justify-center mt-1.5 ml-5">
                          <button
                            class="flex items-center justify-start flex-none w-7 h-7 p-1.5 mr-1 transition-opacity border rounded-md hover:bg-gray-200"
                            @click.stop="updateRecordMetricValue(record)">
                            <Check class="w-5 h-5 text-green-500" aria-hidden="true" />
                          </button>
                          <button
                            class="flex items-center justify-start flex-none w-7 h-7 p-1.5 transition-opacity rounded-md border hover:bg-gray-200"
                            @click.stop="cancelEditMode(metric, record)">
                            <X class="w-5 h-5 text-red-500" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                      <div
                        v-if="
                          metric.reference_name !== utils.getOptionMetricReference(objectType, 'name') &&
                            !record[metric.reference_name]?.edit_mode
                        "
                        @click.stop="openRecordDetailsPage(record)">
                        <button
                          class="opacity-0 hover:scale-125 group-hover/edit:scale-15 group-hover/edit:delay-300 group-hover/edit:duration-300 group-hover/edit:opacity-100 inline-flex items-center justify-center relative hover:bg-gray-200 w-7 h-7 ml-0 p-1.5 border rounded-md focus:outline-none focus:ring-1 focus:ring-light-brand-blue dark:focus:ring-dark-brand-blue">
                          <Eye class="w-5 h-5" aria-hidden="true" color="#899399" />
                        </button>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
        <div
          v-else-if="current_records.length == 0 && !is_loading"
          class="bg-light-box-background dark:bg-dark-box-background flex justify-center min-h-[calc(100%-300px)] w-full absolute">
          <div class="flex flex-col justify-center w-full items-center">
            <Funnel class="h-10 w-10 text-gray-400" />
            <h3 class="mt-2 ml-5 text-2xl font-medium text-gray-500 capitalize whitespace-nowrap">
              {{ $t("No") }}
              {{ $utils.getObjectTypeByType(objectType)?.plural_label || $utils.makePlural(objectType) }}
              {{ $t("found") }}
            </h3>
          </div>
        </div>
        <div
          v-else-if="current_records.length == 0 && isSearching"
          class="bg-white flex justify-center min-h-[calc(100%-100px)] border shadow w-full absolute">
          <div class="flex flex-row justify-center w-full items-center">
            <SearchingIcon class="h-10 w-10 text-gray-400" />
            <h3 class="mt-2 text-2xl font-normal text-gray-500">
              {{ $t("Searching...") }}
            </h3>
          </div>
        </div>
      </table>
    </div>
    <div
      v-show="false"
      class="bg-white flex justify-center border shadow w-full absolute bottom-0"
      :class="{ 'bottom-16': pagination.count > 5 }">
      <div class="flex flex-row justify-between w-full items-end">
        <div class="h-[300px] w-full flex flex-col justify-center bg-gradient-to-tr from-[#2DA7D7]/10 to-transparent">
          <span class="ml-12 text-[26px] font-medium pointer-events-none">{{ $t("Add more companies") }}</span>
          <span class="ml-12 text-[26px] font-medium pointer-events-none">{{ $t("to unlock RevSetter's full power.") }}</span>
          <div
            class="ml-12 pointer-events-default flex items-center border-transparent bg-gradient-to-r from-[#2DA7D7] to-[#841FF4] px-6 mt-9 font-bold text-white rounded-full h-[50px] text-[14px] w-fit hover:cursor-pointer"
            @click="$emitter.emit(ALL_EMITTERS.ADD_NEW_RECORD_MODAL)">
            <FilePlus class="text-white w-5 h-5 ml-1 mr-2" />
            <span>{{ $t("Add Multiple Records") }}</span>
          </div>
        </div>
        <div class="w-1/2 min-h-[300px] relative bg-white pointer-events-none">
          <img class="absolute bottom-0 right-0 flex-shrink-0 h-full" src="@/assets/bg-br-1.png">
          <img
            class="absolute bottom-3 right-40 flex-shrink-0 w-[130px] h-[130px] rounded-full border-[4px] border-white shadow-md-custom object-contain"
            :src="
              competitors[0]?.logo_link
                ? competitors[0].logo_link
                : 'https://storage.googleapis.com/revsetter-public/integrations-logos/amazon-icon.png'
            ">
          <img
            class="absolute bottom-32 right-[180px] flex-shrink-0 w-[90px] h-[90px] rounded-full border-[4px] border-white shadow-md-custom object-contain"
            :src="
              competitors[1]?.logo_link
                ? competitors[1].logo_link
                : 'https://storage.googleapis.com/revsetter-public/integrations-logos/apple-icon.png'
            ">
          <img
            class="absolute bottom-9 right-10 flex-shrink-0 w-[90px] h-[90px] rounded-full border-[4px] border-white shadow-md-custom object-contain"
            :src="
              competitors[2]?.logo_link
                ? competitors[2].logo_link
                : 'https://storage.googleapis.com/revsetter-public/integrations-logos/microsoft-icon.png'
            ">
          <img
            class="absolute bottom-36 right-4 flex-shrink-0 w-[90px] h-[90px] rounded-full border-[4px] border-white shadow-md-custom object-contain"
            :src="
              competitors[3]?.logo_link
                ? competitors[3].logo_link
                : 'https://storage.googleapis.com/revsetter-public/integrations-logos/google-icon.png'
            ">
          <img
            class="absolute bottom-24 right-[88px] flex-shrink-0 w-[110px] h-[110px] rounded-full border-[4px] border-white shadow-md-custom object-contain"
            :src="
              competitors[4]?.logo_link
                ? competitors[4].logo_link
                : 'https://storage.googleapis.com/revsetter-public/integrations-logos/oracle-icon.png'
            ">
        </div>
      </div>
    </div>

    <div v-if="pagination.count > 5" class="absolute w-full h-6 overflow-visible bg-white bottom-8 dark:bg-dark-box-background">
      <PaginationFooter :default-page-size="pageSize" :object="pagination" @update="handlePaginationUpdate" />
    </div>
  </div>
</template>

<script lang="jsx">
import "apex-grid/define"
import draggable from "vuedraggable"
import dataService from "@/services/data.service"
import MetricFieldInput from "@/components/shared/MetricFieldInput.vue"
import NotesIcon from "@/components/shared/icons/NotesIcon.vue"
import RecordAutoCompleteInput from "@/components/shared/RecordAutoCompleteInput.vue"
import {
  Eye,
  ListFilter,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Funnel,
  X,
  Check,
  Plus,
  Pencil,
  Copy,
  FilePlus,
  Lock,
  ExternalLink,
  Clock,
  ArrowLeftFromLine,
  Sigma,
  CheckCheck
} from "lucide-vue-next"
import { VIEW_TYPES } from "@/store/constants/references"
import { utils } from "@/store/helpers"
import Avatar from "./Avatar.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
import { Menu, MenuButton, MenuItems, MenuItem } from "@headlessui/vue"
import TableMetricsFilter from "@/components/shared/TableMetricsFilter.vue"
import { transformMetricFilterToServerFilter, transformServerFilterToMetricFilter } from "@/services/data.transformers"
import SearchingIcon from "@/components/shared/icons/SearchingIcon.vue"
import PaginationFooter from "@/components/shared/PaginationFooter.vue"
import ContextMenu from "@imengyu/vue3-context-menu"
import { h } from "vue"
import { getServiceTitle } from "@/views/dashboard/admin/integrations/ml.js"
import { Record } from "@/models/record"
import IntegrationService from "@/services/integration.service"
import TruncatedHtml from "./TruncatedHtml.vue"

export default {
  components: {
    RecordAutoCompleteInput,
    Funnel,
    SearchingIcon,
    FilePlus,
    Eye,
    Sigma,
    Clock,
    PaginationFooter,
    Check,
    X,
    TableMetricsFilter,
    Pencil,
    NotesIcon,
    ListFilter,
    Plus,
    MetricFieldInput,
    Avatar,
    draggable,
    ChevronUp,
    ChevronDown,
    ChevronsUpDown,
    Lock,
    ArrowLeftFromLine,
    Menu,
    MenuButton,
    MenuItems,
    MenuItem,
    CheckCheck,
    TruncatedHtml
  },
  inject: ["getViewMetrics", "setViewMetrics", "canUserEditLayout", "updateCursorPosition"],

  props: {
    initialRecords: { count: 0, next: null, previous: null, results: [] },
    editTableTrigger: {
      type: Number,
      required: false
    },
    objectType: {
      type: String,
      required: true
    },
    pageReference: {
      type: String,
      required: true
    },
    componentReference: {
      type: String,
      required: false
    },
    adjustable: {
      type: Boolean,
      default: false
    },
    hideCheckboxes: {
      type: Boolean,
      default: false
    },
    isSearching: {
      type: Boolean,
      default: false
    },
    fullFilter: {
      type: String,
      default: ""
    },
    columnsFilter: {
      type: String,
      default: ""
    },
    pageSize: {
      type: Number,
      default: 20
    },
    selectedUsers: {
      type: Array,
      default: null
    },
    disableFetch: {
      type: Boolean,
      default: false
    }
  },
  emits: [
    "update:isSelectAll",
    "update:selectedRecords",
    "update:excludedRecords",
    "update:selectedFullRecords",
    "update:records",
    "update:filter",
    "update:columnsFilter",
    "update:sorting",
    "update:isLoading",
    "dbclick",
    "update:pageSize",
    "update:pageValues",
    "update:metricValue",
    "updated"
  ],
  data() {
    return {
      parent_company: null,
      competitors: [],
      ALL_EMITTERS,

      getServiceTitle,
      Record,
      click_timeout: null,
      click_position: null,
      utils,
      last_clicked_record: null,
      metric_to_be_edited: null,
      page_size: 20,
      current_page: 1,
      activated: false,
      first_loading: false,
      ordering_query: "",
      last_scroll_pos: 0,
      pagination: { count: 0, next: null, previous: null, results: [] },
      current_records: [],
      select_all_visible_records: false,
      select_all_records: false,
      headers: [],
      last_header: [],
      selected_records: new Set(),
      selected_full_records: new Set(),
      drag: false,
      is_loading: false,
      table_metrics: [],
      current_column_filter: null,
      current_column_filter_valid: false,
      excluded_records: [],
      edit_mode: false,
      records_modified: {},
      old_edit_table_trigger: 1,
      prevernt_headers_watch: false,
      formulas_open: []
    }
  },

  computed: {
    all_visible_records_selected() {
      return this.current_records.length == this.current_records.filter((r) => this.selected_records.has(r.id)).length
    },
    // isEndUser() {
    //   return this.$store.state.auth.user.user_type == "end_user"
    // },
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    },
    component_reference_slug() {
      return utils.slugify(this.componentReference)
    },

    dragOptions() {
      return {
        animation: 200,
        group: "records header",
        disabled: !this.adjustable,
        ghostClass: "ghost",
        chosenClass: "chosen"
      }
    }
  },
  watch: {
    headers(newValues, oldValue) {
      if (!this.prevernt_headers_watch) {
        let revert = false
        const reserved_rows = this.headers.filter(
          (h) => h.type == "owner" || h.type == "parent" || h.type == "top_parent" || h.type == "co-owners" || h.type == "tags"
        ).length

        if (reserved_rows == 1 && this.headers[1].type == "metric") {
          revert = true
        }

        if (reserved_rows == 2 && (this.headers[1].type == "metric" || this.headers[2].type == "metric")) {
          revert = true
        }

        if (
          reserved_rows == 3 &&
          (this.headers[1].type == "metric" || this.headers[2].type == "metric" || this.headers[3].type == "metric")
        ) {
          revert = true
        }

        if (revert) {
          this.prevernt_headers_watch = true
          this.headers = oldValue
          setTimeout(() => {
            this.prevernt_headers_watch = false
          }, 600)
        }
      }
    },
    editTableTrigger() {
      //save edits triggered
      if (this.editTableTrigger < this.old_edit_table_trigger) {
        this.updateAllModifiedRecords()
      }
      //edit table triggered
      else {
        this.edit_mode = true
      }
      this.old_edit_table_trigger = this.editTableTrigger
    },
    page_size() {
      this.$emit("update:pageSize", this.page_size)
    },
    is_loading: {
      handler() {
        this.$emit("update:isLoading", this.is_loading)
      }
    },
    table_metrics: {
      deep: true,
      handler() {
        this.refreshTableView()
      }
    },
    select_all_records() {
      this.$emit("update:isSelectAll", this.select_all_records)
      if (!this.select_all_records) {
        this.excluded_records = []
        this.$emit("update:excludedRecords", this.excluded_records)
      }
    },

    initialRecords() {
      if (this.current_page != 1 && this.activated) {
        this.pagination = this.initialRecords
        this.fetchRecords()
        this.activated = false
      } else {
        if (this.initialRecords?.results) {
          this.current_records = this.initialRecords.results.map((m) => {
            m["checked"] = this.selected_records.has(m.id)
            return m
          })
        }
        this.pagination = this.initialRecords
        this.selected_records.clear()
        this.selected_full_records.clear()
      }
    },

    current_records: {
      deep: true,
      handler() {
        this.$emit("update:records", this.current_records)
        if (this.current_records.length == 1) {
          this.getCompanyCompetitors(this.current_records[0].id)
        }
      }
    },
    objectType() {
      this.initTableMetrics()
    }
  },
  activated() {
    this.activated = true
    this.register_listeners()
  },

  deactivated() {
    this.activated = false
    this.first_loading = false
    this.unregister_listeners()
  },
  created() {
    this.register_listeners()
    this.page_size = this.pageSize
    this.refreshTableView()
    this.current_records = this.initialRecords?.results ? this.initialRecords?.results : []
    this.initTableMetrics()
  },

  mounted() {
    const elements = document.getElementsByClassName("col-table-header-btn")
    elements.forEach((el) => {
      el.addEventListener(
        "click",
        (e) => {
          this.updateCursorPosition(e)
        },
        true
      )
    })
  },
  unmounted() {
    this.first_loading = false
    this.unregister_listeners()
  },

  methods: {
    parentChanged(e) {
      if (e == "") {
        this.parent_company = null
      }
    },
    getCompanyCompetitors(id) {
      dataService.getCompanyCompetitors(id).then((res) => {
        if (res.data.results) {
          this.competitors = res.data.results
        } else {
          this.competitors = []
        }
      })
    },
    handleRecordClick(metric, record) {
      if (!this.click_timeout) {
        this.click_timeout = setTimeout(() => {
          // simple click
          this.click_timeout = null
          clearTimeout(this.click_timeout)
        }, 500) //tolerance in ms
      } else {
        clearTimeout(this.click_timeout)
        this.click_timeout = null
        this.toggleEditMode(metric, record)
        // double click
      }
    },
    setFormulaOpen(metric, record) {
      this.$emitter.emit(ALL_EMITTERS.NESTED_METRIC_DETAIL_MODAL, {
        metric: metric,
        record: record
      })
    },
    handleSelectOrUnselectAllRecords() {
      if (!this.select_all_records) {
        this.select_all_records = true
        this.excluded_records = []
      } else {
        if (this.excluded_records.length) {
          this.excluded_records = []
        } else {
          this.select_all_records = false
        }
      }
    },
    handleUnSelectVisibleRecords() {
      this.current_records = this.current_records.map((company) => {
        company["checked"] = false
        return company
      })
      this.current_records.forEach((e) => this.selected_records.delete(e.id))
      this.current_records.forEach((e) => this.selected_full_records.delete(e))
      this.$emit("update:selectedRecords", Array.from(this.selected_records))
      this.$emit("update:selectedFullRecords", Array.from(this.selected_full_records))
    },
    handleSelectVisibleRecords() {
      this.current_records = this.current_records.map((company) => {
        company["checked"] = true
        return company
      })
      this.current_records.forEach((e) => this.selected_records.add(e.id))
      this.current_records.forEach((e) => this.selected_full_records.add(e))
      this.$emit("update:selectedRecords", Array.from(this.selected_records))
      this.$emit("update:selectedFullRecords", Array.from(this.selected_full_records))
    },

    handlePaginationUpdate({ page, page_size }) {
      this.current_page = page
      this.page_size = page_size
      if (!this.disableFetch) {
        this.fetchRecords()
        this.$emit("update:pageSize", page_size)
      } else {
        this.$emit("update:pageValues", { page_size, page_number: page })
      }
    },
    updateNewMetricValue(e) {
      this.metric_to_be_edited.value = this.$utils.clone(e)
    },
    updateRecordMetricValue(record) {
      if (this.metric_to_be_edited.id == "parent__id") {
        this.$store
          .dispatch("data/updateRecord", {
            record_id: record.id,
            data: { parent: this.parent_company?.id ? this.parent_company.id : null }
          })
          .then((res) => {
            let index = this.current_records.findIndex((r) => r.id == record.id)
            if (index != -1) {
              this.current_records.splice(index, 1, res.data)
            }
            if (this.$store.state.ui.currentCompany) {
              this.$store.dispatch("ui/setCurrentCompany", res.data)
            }
            record[this.metric_to_be_edited?.reference_name] = {
              edit_mode: false
            }
            this.metric_to_be_edited = null
          })
      } else {
        const metric_clone = this.$utils.clone(this.metric_to_be_edited)
        this.metric_to_be_edited = null
        let new_value = metric_clone?.value
        let prev_value = record.values[metric_clone.reference_name]
        // if no change not need to to update
        if (prev_value == new_value) {
          record[metric_clone.reference_name] = {
            edit_mode: false
          }
          return
        }

        if (metric_clone.data_type == "Date" || metric_clone.data_type == "DateTime") {
          new_value = utils.convertDateStringFromLocalDateToUtc(new_value, metric_clone.data_type)
        }

        record.values[metric_clone.reference_name] = new_value

        this.$store
          .dispatch("data/updateRecordMetric", {
            record_id: record.id,
            data: {
              prev_value: prev_value,
              new_value: new_value,
              metric: metric_clone.id
            }
          })
          .then((res) => {
            this.$emit("update:metricValue", metric_clone?.value)
            let index = this.current_records.findIndex((r) => r.id == record.id)
            if (index != -1) {
              this.current_records.splice(index, 1, res.data)
            }
            if (this.$store.state.ui.currentCompany) {
              this.$store.dispatch("ui/setCurrentCompany", res.data)
            }

            record.values = res.data.values

            this.$store.dispatch("data/emitRecordUpdatedEmitters", res.data)
            this.$emitter.emit(ALL_EMITTERS.REFRESH_ROLLUP_METRICS, this.componentReference)
            this.$emitter.emit(ALL_EMITTERS.REFRESH_ROLLUP_METRICS, record.id)
            this.$emitter.emit(ALL_EMITTERS.REFETCH_ROLLUP_METRIC, metric_clone)
            this.$emitter.emit(ALL_EMITTERS.INIT_PHASES_IN_CUSTOMER_JOURNEYS_VIEW)
            this.$emitter.emit(ALL_EMITTERS.INIT_PHASES_IN_JOURNEYS)
            this.$emit("updated")
            this.$emitter.emit(ALL_EMITTERS.GRAPHS_SHOW_UPDATED)
            record[metric_clone?.reference_name] = {
              edit_mode: false
            }
          })
      }
    },
    cancelEditMode(metric, record) {
      record[metric.reference_name].edit_mode = false
      this.metric_to_be_edited = null
    },
    /* Switch edit mode for specific cell */
    toggleEditMode(metric, record) {
      if (metric.id == "parent__id") {
        this.metric_to_be_edited = metric
        if (record?.parent) {
          dataService.getRecord(record.parent).then((result) => {
            this.parent_company = result.data
            if (!metric?.is_read_only) {
              record[metric.reference_name] = { edit_mode: true }
            }
          })
        } else {
          this.parent_company = null
          if (!metric?.is_read_only) {
            record[metric.reference_name] = { edit_mode: true }
          }
        }
      } else {
        this.metric_to_be_edited = {
          ...metric,
          original_value: record.values[metric.reference_name],
          value: record.values[metric.reference_name]
        }
        if (!metric?.is_read_only) {
          // if initial edit toggle we append the flags to the record
          record[metric.reference_name] = { edit_mode: true }
        }
      }
    },

    updateAllModifiedRecords() {
      const promises = []
      Object.keys(this.records_modified).forEach((record_id) => {
        const new_values = this.records_modified[record_id]
        promises.push(
          this.$store.dispatch("data/updateRecord", {
            record_id: record_id,
            data: { values: new_values }
          })
        )
      })

      Promise.all(promises).then((values) => {
        values.forEach((res) => {
          const record = res.data
          const index = this.current_records.findIndex((r) => r.id == record.id)
          this.current_records.splice(index, 1, record)
        })
        this.postRecordsUpdate(this.$utils.clone(this.records_modified))
        this.records_modified = {}
        this.edit_mode = false
      })
    },
    openEditMetric(metric) {
      if (metric.metric_class == "computed_metric") {
        this.$emitter.emit(ALL_EMITTERS.EDIT_COMPUTED_METRIC, {
          metric: metric
        })
      } else {
        this.$emitter.emit(ALL_EMITTERS.EDIT_CUSTOM_METRIC, {
          metric: metric
        })
      }
    },

    highlightRelatedMetrics(metric) {
      metric["is_highlighted"] = true
      if (metric.included_metrics) {
        metric.included_metrics.forEach((m_id) => {
          if (this.headers.find((h) => h.id == m_id)) {
            this.headers.find((h) => h.id == m_id).is_highlighted = true
          }
        })
      }
      setTimeout(() => {
        metric.is_highlighted = false
        if (metric.included_metrics) {
          metric.included_metrics.forEach((m_id) => {
            if (this.headers.find((h) => h.id == m_id)) {
              this.headers.find((h) => h.id == m_id).is_highlighted = false
            }
          })
        }
      }, 1000)
    },
    handleRemoveColumnFilter(element, closeFilterModal, close) {
      const columns_filter_transformed = transformServerFilterToMetricFilter(this.columnsFilter).filter
      const index = columns_filter_transformed.findIndex((f) => f.reference_name == element.reference_name)
      if (index == 1 && columns_filter_transformed[index + 1]?.item_type == "operator") {
        if (!columns_filter_transformed[index + 2]?.reference_name) {
          columns_filter_transformed.splice(index, 3)
        } else {
          columns_filter_transformed.splice(index, 2)
        }
      } else if (columns_filter_transformed[index - 1]?.item_type == "operator") {
        columns_filter_transformed.splice(index - 1, 2)
      } else if (
        columns_filter_transformed[index - 1]?.item_type == "clause" &&
        columns_filter_transformed[index - 2]?.item_type == "operator"
      ) {
        columns_filter_transformed.splice(index - 2, 3)
      } else {
        columns_filter_transformed.splice(index, 1)
      }

      this.$emit("update:columnsFilter", transformMetricFilterToServerFilter(columns_filter_transformed).filter, true)
      closeFilterModal()
      close()

      // this.$emit("filter", this.filter)
    },

    getColumnFilterFromFilter(element) {
      let extracted_metric_from_filter
      if (this.columnsFilter?.includes(element.reference_name)) {
        extracted_metric_from_filter = transformServerFilterToMetricFilter(this.columnsFilter).filter.find(
          (f) => f.reference_name == element.reference_name
        )

        if (extracted_metric_from_filter) {
          return transformMetricFilterToServerFilter([extracted_metric_from_filter], true).filter
        }
        return null
      } else if (element.reference_name == "parent__id" && this.columnsFilter?.includes("parent__name")) {
        extracted_metric_from_filter = transformServerFilterToMetricFilter(this.columnsFilter).filter.find(
          (f) => f.reference_name == "parent__name"
        )
        if (extracted_metric_from_filter) {
          return transformMetricFilterToServerFilter([extracted_metric_from_filter], true).filter
        }
      }
      return null
    },

    handleChangeColumnFilter(column_filter) {
      this.current_column_filter = column_filter
    },
    handleSubmitColumnFilter(closeFilterModal, close) {
      const columns_filter_transformed = transformServerFilterToMetricFilter(this.columnsFilter)
      const current_column_filter_transformed = transformServerFilterToMetricFilter(this.current_column_filter)

      const current_column_metric = current_column_filter_transformed.filter[1]

      let columns_filter_calc = ""
      if (!columns_filter_transformed.filter.length) {
        columns_filter_calc = this.current_column_filter
      } else {
        let current_column_metric_found_in_filter = false
        columns_filter_transformed.filter.forEach((f, index) => {
          if (f?.reference_name == current_column_metric?.reference_name) {
            columns_filter_transformed.filter.splice(index, 1, current_column_metric)
            current_column_metric_found_in_filter = true
          }
        })

        if (!current_column_metric_found_in_filter) {
          columns_filter_calc = `${this.columnsFilter} and ${this.current_column_filter}`
        } else {
          columns_filter_calc = transformMetricFilterToServerFilter(columns_filter_transformed.filter).filter
        }
      }

      this.$emit("update:columnsFilter", columns_filter_calc)

      closeFilterModal()
      close()
    },

    recordValueWithinFilter(filter, record) {
      const metric_filter = transformServerFilterToMetricFilter(filter)
      let should_add_record = true
      metric_filter.filter.forEach((filter) => {
        if (filter.reference_name) {
          const operator = filter.operator.value
          if (operator == ">") {
            if (!(parseInt(record.values[filter.reference_name]) > parseInt(filter.value))) {
              should_add_record = false
            }
          }
          if (operator == "<") {
            if (!(parseInt(record.values[filter.reference_name]) < parseInt(filter.value))) {
              should_add_record = false
            }
          }
          if (operator == "==") {
            if (!(parseInt(record.values[filter.reference_name]) == parseInt(filter.value))) {
              should_add_record = false
            }
          }

          if (operator == "!=") {
            if (!(parseInt(record.values[filter.reference_name]) !== parseInt(filter.value))) {
              should_add_record = false
            }
          }

          if (operator == ">=") {
            if (!(parseInt(record.values[filter.reference_name]) >= parseInt(filter.value))) {
              should_add_record = false
            }
          }
          if (operator == "<=") {
            if (!(parseInt(record.values[filter.reference_name]) <= parseInt(filter.value))) {
              should_add_record = false
            }
          }

          if (operator == "__IS_NULL__") {
            if (!parseInt(record.values[filter.reference_name])) {
              should_add_record = false
            }
          }
        }
      })
      return should_add_record
    },
    register_listeners() {
      // this.registerScrollListener()

      this.$emitter?.on(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_ADVANCED_COMPANIES_METRICS_TABLE, () => {
        this.initTableMetrics()
      })
      this.$emitter?.on(ALL_EMITTERS.RECORD_ADDED, (record) => {
        let should_add_record = true
        if (this.fullFilter) {
          should_add_record = this.recordValueWithinFilter(this.fullFilter, record)
        }
        if (this.objectType == record.type && should_add_record) {
          this.$emitter?.emit(ALL_EMITTERS.INIT_PHASES_IN_JOURNEYS)
          this.current_records.unshift(record)
        }
      })

      this.$emitter?.on(ALL_EMITTERS.UPDATE_COUNT_NOTES_IN_RECORDS_TABLE, ({ id: id, count: count }) => {
        let index = this.current_records.findIndex((record) => record.id == id)
        if (index != -1) {
          this.current_records[index]["notes_summary"]["count"] = count
        }
      })

      this.$emitter?.on(ALL_EMITTERS.RECORD_UPDATED_IN_OVERVIEW_COMPONENT, (record) => {
        let index = this.current_records.findIndex((r) => r.id == record.id)
        if (index != -1) {
          this.current_records[index] = record
        }
      })

      if (!this.first_loading) {
        this.first_loading = true
        this.$emitter?.on(ALL_EMITTERS.EXPORT_TABLE_TO_CSV, (filter) => {
          if (this.componentReference == filter.component_reference) {
            dataService.exportRecords(filter).then(() => {
              this.$toast.open({
                message: this.$t("Export has started, you will receive an email with the download link"),
                position: "top",
                type: "success",
                duration: 5000
              })
            })
          }
        })
      }
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.PORTFOLIO_UPDATED_IN_ADVANCED_COMPANIES_METRICS_TABLE)
      this.$emitter.off(ALL_EMITTERS.RECORD_ADDED)
      this.$emitter.off(ALL_EMITTERS.RECORD_UPDATED_IN_OVERVIEW_COMPONENT)
      this.$emitter.off(ALL_EMITTERS.UPDATE_COUNT_NOTES_IN_RECORDS_TABLE)
      this.$emitter.off(ALL_EMITTERS.EXPORT_TABLE_TO_CSV)
    },
    showAllAssignedTags(record) {
      if (record?.is_all_assigned_tags_shown) {
        record["is_all_assigned_tags_shown"] = false
      } else {
        record["is_all_assigned_tags_shown"] = true
      }
    },

    initTableMetrics() {
      const metrics_obj = {
        page_reference: this.pageReference,
        object_type: this.objectType,
        view_type: VIEW_TYPES.TABLE,
        flat: false
      }
      if (this.$route.params?.page) {
        metrics_obj["layout_page_reference"] = this.$route.params?.page
      }
      if (this.componentReference) {
        metrics_obj["component_reference"] = this.componentReference
      }
      this.table_metrics = this.getViewMetrics(metrics_obj)
    },
    postRecordsUpdate(records) {
      const metrics_list = []
      Object.keys(records).forEach((record_id) => {
        if (this.$store.state.ui.currentCompany.id == record_id) {
          this.$store.dispatch("ui/setCurrentCompany", {
            ...this.current_records.find((r) => r.id == record_id),
            values: this.current_records.find((r) => r.id == record_id).values
          })
          this.$store.dispatch(
            "data/emitRecordUpdatedEmitters",
            this.current_records.find((r) => r.id == record_id)
          )

          const record_metrics = records[record_id]
          Object.keys(record_metrics).forEach((metric_reference_name) => {
            if (!metrics_list.includes(metric_reference_name)) {
              metrics_list.push(metric_reference_name)
            }
          })
        }
      })

      metrics_list.forEach((metric_ref_name) => {
        this.$emitter.emit(ALL_EMITTERS.REFETCH_ROLLUP_METRIC, this.$utils.getMetricByReferenceName(metric_ref_name))
      })
      // this.$emitter.emit(ALL_EMITTERS.REFRESH_ROLLUP_METRICS, this.componentReference)

      this.$emitter.emit(ALL_EMITTERS.INIT_PHASES_IN_CUSTOMER_JOURNEYS_VIEW)
      this.$emitter.emit(ALL_EMITTERS.INIT_PHASES_IN_JOURNEYS)
      this.$emit("updated")
      this.$emitter.emit(ALL_EMITTERS.GRAPHS_SHOW_UPDATED)
    },

    /* Switch edit mode for specific cell */

    freezeColumn(id, close) {
      let object_type_index = this.$store.state.ui_options?.frozen_columns?.findIndex((x) => x.object_type == this.objectType)
      let data = this.$store.state.ui_options?.frozen_columns
      // If there is no freezen column for this object type
      if (object_type_index == undefined || object_type_index == -1) {
        if (data && data.length > 0) {
          data.push({ object_type: this.objectType, id: id })
        } else {
          data = [{ object_type: this.objectType, id: id }]
        }
      } else {
        // Unfreezing this column or replace it by other column
        if (this.$store.state.ui_options.frozen_columns[object_type_index].id == id) {
          data.splice(object_type_index, 1)
        } else {
          data[object_type_index].id = id
        }
      }
      this.$store.dispatch("ui/setUIValue", { frozen_columns: data })
      close()
    },

    hexToRgb(hex) {
      var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result
        ? {
            r: parseInt(result[1], 16),
            g: parseInt(result[2], 16),
            b: parseInt(result[3], 16)
          }
        : {
            r: 220,
            g: 220,
            b: 220
          }
    },

    registerScrollListener() {
      this.$nextTick(() => {
        document.getElementById(`table-${this.pageReference}-${this.objectType}`)?.addEventListener(
          "scroll",
          () => {
            let element = document.getElementById(`table-${this.pageReference}-${this.objectType}`)
            if (this.last_scroll_pos < element.scrollTop) {
              this.last_scroll_pos = element.scrollTop
            }
          },
          false
        )
      })
    },
    updateCurrentViewMetrics() {
      let updated_table_metrics = this.headers.filter((m) => m.type == "metric" || m.type == "field")
      if (
        !utils.isEqual(
          this.table_metrics.map((m) => m.id),
          updated_table_metrics.map((m) => m.id)
        )
      ) {
        const metrics_obj = {
          page_reference: this.pageReference,
          object_type: this.objectType,
          view_type: VIEW_TYPES.TABLE,
          new_metrics_list: updated_table_metrics.map((x) => x.reference_name)
        }
        if (this.$route.params?.page) {
          metrics_obj["layout_page_reference"] = this.$route.params?.page
        }
        if (this.componentReference) {
          metrics_obj["component_reference"] = this.componentReference
        }
        this.setViewMetrics(metrics_obj)
        if (!(this.pageReference == "company_profile" && this.objectType == "contact")) {
          this.$emitter.emit(ALL_EMITTERS.METRICS_CHANGED)
        }
      }
    },
    sortRecords() {
      this.last_header = utils.clone(this.headers)
      this.ordering_query = this.headers
        .filter(
          (h) =>
            h.type == "metric" ||
            (h.type == "field" && h.sortable) ||
            h.reference_name == "owner__id" ||
            h.reference_name == "parent__id" ||
            h.reference_name == "parent__name" ||
            h.reference_name == "top_parent"
        )
        .filter((el) => el.sort != 0)
        .sort((a, b) => a.sort_index - b.sort_index)
        .map(
          (el) =>
            `${String(el.sort).replace("1", "")}${
              el.reference_name == "owner__id"
                ? "owner"
                : el.reference_name == "parent__id"
                  ? "parent"
                  : el.reference_name == "top_parent"
                    ? "top_parent"
                    : el.reference_name
            }`
        )
        .join(",")
      this.$emit("update:sorting", this.ordering_query)
    },

    async fetchRecords() {
      if (!this.is_loading) {
        this.is_loading = true
        if (this.pagination?.next || this.pagination?.previous) {
          let filters = null

          if (this.fullFilter.length > 0) {
            filters = {}
            filters["filter"] = this.fullFilter
            filters["object_type"] = this.objectType
          }
          if (this.selectedUsers?.length > 0) {
            if (filters == null) {
              filters = {}
            }
            filters["owners"] = this.selectedUsers
            filters["object_type"] = this.objectType
          }
          // find and replace page_size=<number> by this.page_size from url and leave the rest of the query params
          let url
          if (this.pagination.next) {
            url = this.pagination.next
          } else {
            url = this.pagination.previous
          }
          url = url.replace(/page_size=\d+/, `page_size=${this.page_size}`).replace(/page=\d+/, `page=${this.current_page}`)
          dataService
            .getRecordsByUrl(url, filters)
            .then((result) => {
              this.pagination = result.data
              this.current_records = result.data.results
              this.is_loading = false
            })
            .catch(() => {
              this.is_loading = false
            })
        }
      }
    },
    refreshTableView() {
      this.headers = [{ type: "checkbox", id: "_checkbox" }]

      this.headers = this.headers.concat(
        this.table_metrics.map((metric) => {
          let last_header_sort = this.last_header.find((met) => met.id == metric.id)?.sort || 0
          return { sort: last_header_sort, type: "metric", ...metric }
        })
      )
    },
    async toggleRecordFromSelectedList(record, $event) {
      // if the ctrl key is pressed, get the last clicked row and select/ deselect all the rows between the last clicked row and the current clicked row
      if ($event.ctrlKey) {
        let new_status = !this.selected_records.has(record.id)
        let last_clicked_index =
          (this.last_clicked_record && this.current_records.findIndex((r) => r.id == this.last_clicked_record.id)) || 0
        let current_clicked_index = this.current_records.findIndex((r) => r.id == record.id)
        let start = Math.min(last_clicked_index, current_clicked_index)
        let end = Math.max(last_clicked_index, current_clicked_index)
        if (new_status) {
          for (let i = start; i <= end; i++) {
            this.current_records[i]["checked"] = true
            this.selected_records.add(this.current_records[i].id)
            this.selected_full_records.add(this.current_records[i])
          }
        } else {
          for (let i = start; i <= end; i++) {
            this.current_records[i]["checked"] = false
            this.selected_records.delete(this.current_records[i].id)
            this.selected_full_records.delete(this.current_records[i])
          }
        }
      } else {
        if (this.selected_records.has(record.id)) {
          record["checked"] = false
          this.selected_full_records.delete(record)
          this.selected_records.delete(record.id)
        } else {
          record["checked"] = true
          this.selected_records.add(record.id)
          this.selected_full_records.add(record)
        }
      }
      this.last_clicked_record = record

      this.$emit("update:selectedRecords", Array.from(this.selected_records))
      this.$emit("update:selectedFullRecords", Array.from(this.selected_full_records))
    },

    toggleRecordFromSelectedAllList(record) {
      const index = this.excluded_records.findIndex((r) => r == record.id)
      if (index !== -1) {
        this.excluded_records.splice(index, 1)
      } else {
        this.excluded_records.push(record.id)
      }

      this.$emit("update:excludedRecords", this.excluded_records)
    },
    handleContextMenu(e, record) {
      e.preventDefault()
      let rs_url_label = h(
        "div",
        {
          style: {
            display: "flex",
            fontSize: "14px",
            whiteSpace: "nowrap",
            overflow: "hidden",
            width: "250px",
            maxWidth: "250px",
            textOverflow: "ellipsis",
            alignItems: "center"
          }
        },
        [
          h(
            "span",
            {
              style: { flex: "1", overflow: "hidden", textOverflow: "ellipsis" }
            },
            "Copy URL"
          ),
          h(
            "span",
            {
              style: {
                flexShrink: "0",
                marginLeft: "1px",
                padding: "2px 2px",
                color: "#333",
                textTransform: "uppercase"
              }
            },
            "⌘"
          )
        ]
      )

      let items = [
        {
          label: "Copy name",
          icon: <Copy class="w-4 h-4 text-gray-400" />,
          onClick: () => this.performContextMenuAction("copy_name", record)
        },
        {
          label: "Open",
          icon: <ExternalLink class="w-4 h-4 text-gray-400" />,
          onClick: () => this.performContextMenuAction("open_url", record)
        },
        {
          label: rs_url_label,
          icon: <Copy class="w-4 h-4 text-gray-400" />,
          onClick: (e) => this.performContextMenuAction("copy_url", record, null, e.ctrlKey)
        }
      ]

      if (record?.origins) {
        items = items.concat(
          Object.entries(record.origins)
            .filter(([name]) => name !== "platform" && name !== "rs_datahub")
            .map(([name, value]) => {
              let integration = this.$store.state.integration.integrations.find((i) => i.name === name)
              let integration_name = integration ? integration.title : name

              let label = h(
                "div",
                {
                  style: {
                    display: "flex",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    width: "250px",
                    maxWidth: "250px",
                    textOverflow: "ellipsis",
                    alignItems: "center"
                  }
                },
                [
                  h(
                    "span",
                    {
                      style: { flex: "1", overflow: "hidden", textOverflow: "ellipsis" }
                    },
                    `Copy ${integration_name} URL`
                  ),
                  h(
                    "span",
                    {
                      style: {
                        flexShrink: "0",
                        marginLeft: "1px",
                        padding: "2px 2px",
                        color: "#333",
                        textTransform: "uppercase"
                      }
                    },
                    "⌘"
                  )
                ]
              )

              return {
                label: label,
                icon: <Copy class="w-4 h-4 text-gray-400" />,
                onClick: (e) => this.performContextMenuAction("copy_origin_url", record, { name, id: value }, e.ctrlKey)
              }
            })
        )
      }
      if (record?.origins) {
        items = items.concat(
          Object.entries(record.origins)
            .filter(([name]) => name == "salesforce" || name == "hubspot")
            .map(([name, value]) => {
              let integration = this.$store.state.integration.integrations.find((i) => i.name === name)
              let integration_name = integration ? integration.title : name

              let label = h(
                "div",
                {
                  style: {
                    display: "flex",
                    fontSize: "14px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    width: "250px",
                    maxWidth: "250px",
                    textOverflow: "ellipsis",
                    alignItems: "center"
                  }
                },
                [
                  h(
                    "span",
                    {
                      style: { flex: "1", overflow: "hidden", textOverflow: "ellipsis" }
                    },
                    `Show ${integration_name} Origin Data`
                  ),
                  h(
                    "span",
                    {
                      style: {
                        flexShrink: "0",
                        marginLeft: "1px",
                        padding: "2px 2px",
                        color: "#333",
                        textTransform: "uppercase"
                      }
                    },
                    "⌘"
                  )
                ]
              )

              return {
                label: label,
                icon: <Copy class="w-4 h-4 text-gray-400" />,
                onClick: (e) => this.performContextMenuAction("copy_main_integration_data", record, { name, id: value }, e.ctrlKey)
              }
            })
        )
      }

      ContextMenu.showContextMenu({
        x: e.x,
        y: e.y,
        closeWhenScroll: false,
        items: items
      })
    },

    performContextMenuAction(action, record, origin = null, isCtrlPressed = null) {
      // get record data from the service
      let record_url =
        record.type === "company"
          ? `${window.location.origin}/portfolio/${record.type}/${record.id}/`
          : `${window.location.origin}/portfolio/${record.parent_type}/${record.parent}/${record.type}/${record.id}/`

      if (action === "copy_url") {
        if (isCtrlPressed) {
          window.open(record_url, "_blank")
        } else {
          navigator.clipboard.writeText(record_url)
          this.$toast.open({
            message: "URL copied to clipboard",
            position: "top",
            type: "success",
            duration: 1000
          })
        }
      } else if (action == "copy_name") {
        navigator.clipboard.writeText(this.$utils.getRecordName(record))
        this.$toast.open({
          message: "Name copied to clipboard",
          position: "top",
          type: "success",
          duration: 1000
        })
      } else if (action === "copy_main_integration_data") {
        // get record data from the service
        let integration = this.$store.state.integration.integrations.find((i) => i.name === origin.name)
        let object_name = this.$store.state.object_types
          .find((o) => o.type === record.type)
          .origins_types.find((o) => o.origin === origin.name).type
        IntegrationService.getServiceRecord(integration.id, { record_id: origin.id, object_name: object_name })
          .then((res) => {
            console.debug(res.data)
            if (res.data) {
              //convert res.data to string
              let data = ""
              Object.entries(res.data).forEach(([key, value]) => {
                data += `${key}: ${value}<br>`
              })
              data = `<div class="prose prose-slate">${data}</div>`
              navigator.clipboard.writeText(data)
              this.$toast.open({
                message: data,
                position: "top",
                type: "info",
                duration: 0,
                dismissible: true
              })
            }
          })
          .catch((err) => {
            console.error(err)
          })
      } else if (action === "copy_origin_url") {
        let origin_url = origin.id
        let integration = this.$store.state.integration.integrations.find((i) => i.name === origin.name)
        let instance_url = integration ? integration.instance_url : null

        if (origin.name.includes("salesforce")) {
          origin_url = `${instance_url}/lightning/r/Account/${origin.id}/view`
        }

        if (isCtrlPressed && origin.id !== origin_url) {
          window.open(origin_url, "_blank")
        } else {
          navigator.clipboard.writeText(origin_url)
          this.$toast.open({
            message: "Origin URL copied to clipboard",
            position: "top",
            type: "success",
            duration: 1000
          })
        }
      } else {
        window.open(record_url, "_blank")
      }
    },
    async openRecordDetailsPage(record, metric) {
      if (!this.click_timeout) {
        this.click_timeout = setTimeout(async () => {
          // simple click
          this.click_timeout = null
          clearTimeout(this.click_timeout)

          if (this.$isDragging) {
            return
          }

          if (!this.metric_to_be_edited || !record[this.metric_to_be_edited?.reference_name]?.edit_mode) {
            this.$utils.Record(record).open()
          }
        }, 500) //tolerance in ms
      } else {
        clearTimeout(this.click_timeout)
        this.click_timeout = null
        this.toggleEditMode(metric, record)
        // double click
      }
    },
    openRecordParentDetailsPage(record) {
      this.$router.push(`/portfolio/${record.parent_type}/${record.parent}/`)
    },
    formatMetricDateValue(record, metric) {
      const date = new Date(record.values[metric.reference_name])
      const options = { year: "numeric", month: "short", day: "numeric" }
      if (metric.data_type == "DateTime") {
        options.hour = "numeric"
        options.minute = "numeric"
      }
      return date.toLocaleDateString("en-us", options)
    },

    openObjectReferenceObjectPage(record, object_reference) {
      if (object_reference.object_type == "company") {
        this.$router.push(`/portfolio/company/${object_reference.origin_id}/`)
      } else if (object_reference.object_type == "user") {
        this.$router.push(`/portfolio//company/${record.id}/`)
      } else {
        this.$router.push(`/portfolio/${record.type}/${record.id}/${object_reference.object_type}/${object_reference.origin_id}/`)
      }
    },

    openModalNotes(record) {
      this.$emitter.emit(ALL_EMITTERS.ADD_NOTES_RECORD, { company: record })
    }
  }
}
</script>
